// SUBFROST AMM (factory 4:65522) client-side quote math.
//
// Constant-product with the fee applied to the input, matching the on-chain
// pool byte-for-byte (calibrated in arb-core against live simulate results —
// fee is per-1000 from pool op-20, default 10 = 1.00%):
//
//   inWithFee = amountIn * (1000 - feePer1000)
//   out       = floor(inWithFee * reserveOut / (reserveIn * 1000 + inWithFee))
//
// All amounts are raw base units (divisibility 8) as bigint.
//
// SUBFROSTAMM (factory 4:333052) pools quote with the SAME constant-product
// formula at a parts-per-million fee (lp + protocol + season, 15000 = 1.5% on
// every mainnet pool): the hop carries `feeNum / feeDen` so one formula serves
// both venues. Verified bit-exact against a mainnet simulate 2026-10-05
// (0.1 DIESEL → 4481 frBTC sats through 2:96884).

export interface PoolToken {
	id: string
	name: string | null
	icon: string | null
}

export interface Pool {
	pool_id: string
	token0: PoolToken
	token1: PoolToken
	reserve0: string
	reserve1: string
	total_supply: string
	fee_per_1000: number
	name: string | null
	/// 24h price change (reserve1/reserve0), server-computed from the permanent
	/// reserve history. Null when that pool has no history near 24h ago — the
	/// UI must render "—", never a fabricated 0%.
	change_24h_pct?: number | null
	/// Absent = an oyl pool (factory 4:65522). `subfrostamm` pools come from
	/// `/alkane/pools` `sfamm_pools`, quote at `fee_ppm`, and swap ONLY through
	/// the SUBFROSTAMM factory — never their own pool id.
	venue?: Venue
	/// SUBFROSTAMM fee, parts per million (lp + protocol + season).
	fee_ppm?: number
}

export type Venue = 'oyl' | 'subfrostamm'

export function poolVenue(pool: Pool): Venue {
	return pool.venue === 'subfrostamm' ? 'subfrostamm' : 'oyl'
}

interface Hop {
	pool: Pool
	venue: Venue
	tokenIn: string
	tokenOut: string
	reserveIn: bigint
	reserveOut: bigint
	/// Pool fee as feeNum / feeDen: per-1000 on oyl, per-million on SUBFROSTAMM.
	feeNum: bigint
	feeDen: bigint
	amountIn: bigint
	amountOut: bigint
}

/// A hop's pool fee in percent, for display ("1%", "1.5%").
export function hopFeePct(h: { feeNum: bigint; feeDen: bigint }): number {
	return (Number(h.feeNum) * 100) / Number(h.feeDen)
}

/// One hop's pool, in the shape OWRouter's `route` cellpack wants it.
interface QuotePool {
	/// "block:tx" of the pool this hop swaps through.
	poolId: string
	/// True when this hop's OUTPUT token is the pool's token1. The router takes
	/// this bit per hop to know which side of the pool to draw from; a wrong bit
	/// fails the pool's k-invariant and the route refunds rather than misfills.
	outSlot: boolean
}

export interface Quote {
	path: string[]
	hops: Hop[]
	/// The route's pools in order — exactly one per hop, so
	/// `pools.length + 1 === path.length`. This is what the swap request sends
	/// to the router; without it the API falls back to the AMM factory.
	pools: QuotePool[]
	amountIn: bigint
	/// What the user actually receives: NET of the router's fee.
	amountOut: bigint
	/// Output at depth-free spot price — reference for price impact. GROSS: it
	/// is a market price, not an execution quote, and the mid-price helpers
	/// below read it to value holdings.
	spotOut: bigint
	priceImpactPct: number
}

export const FRBTC_ID = '32:0'
export const BTC_ID = 'BTC'
export const FRUSD_ID = '4:1776'
/// Subfrost's frBTC/frUSD CryptoSwap pool — also its own LP token
/// ("frBTCUSD"), which is 18-decimal by construction, the lone exception to
/// the alkanes-wide 8-dec convention.
export const CURVE_LP_ID = '4:1778'

/// frBTC's asset-page framing: acquiring frBTC IS wrapping BTC (1:1 via
/// subfrost) and disposing is unwrapping, so every surface on that page says
/// wrap/unwrap where other assets say buy/sell. One helper so the segmented
/// control, sheets, tape badges, and stat bars can never disagree.
export function sideLabels(assetId: string | null | undefined): {
	buy: string
	sell: string
	buys: string
	sells: string
	Buy: string
	Sell: string
} {
	if (assetId === FRBTC_ID) {
		return {
			buy: 'wrap',
			sell: 'unwrap',
			buys: 'wraps',
			sells: 'unwraps',
			Buy: 'Wrap',
			Sell: 'Unwrap'
		}
	}
	return { buy: 'buy', sell: 'sell', buys: 'buys', sells: 'sells', Buy: 'Buy', Sell: 'Sell' }
}

/// Canonical launch assets. Anything else wearing these names is an impostor —
/// alkane ids are the only identity that matters, names are unconstrained and
/// collide freely on-chain. Admin-approved additions come from the API registry.
export const VERIFIED_ASSETS: Record<string, string> = {
	'32:0': 'frBTC',
	'2:0': 'DIESEL',
	'4:1776': 'frUSD',
	'2:490': 'CheekyB',
	'2:16': 'METHANE',
	'2:25720': 'ALKAMIST',
	'2:25349': 'ARBUZ',
	[BTC_ID]: 'BTC'
}

export function isVerified(id: string, additionalIds: Iterable<string> = []): boolean {
	if (Object.prototype.hasOwnProperty.call(VERIFIED_ASSETS, id)) {
		return true
	}
	for (const candidate of additionalIds) {
		if (candidate === id) {
			return true
		}
	}
	return false
}

/// A token is an impostor when its NAME matches a verified asset's name but
/// its id does not. These are the rug shape: `frBTC 2:62280` is not frBTC.
/// Names shared by more than one token id in the live set. An allowlist of two
/// canonical assets was never enough: the live pool set carries TORTILLA x4,
/// bUSD x2, pi x3, goldane, acorns and pizza — indistinguishable rows whose
/// only difference is a 12px id.
export function ambiguousNames(tokens: { id: string; name: string | null }[]): Set<string> {
	const byName = new Map<string, Set<string>>()
	for (const t of tokens) {
		const n = t.name?.trim().toLowerCase()
		if (!n) {
			continue
		}
		const ids = byName.get(n) ?? new Set<string>()
		ids.add(t.id)
		byName.set(n, ids)
	}
	const out = new Set<string>()
	for (const [name, ids] of byName) {
		if (ids.size > 1) {
			out.add(name)
		}
	}
	return out
}

export function impersonatesVerified(
	token: { id: string; name: string | null },
	additionalVerified: { id: string; name: string | null }[] = []
): string | null {
	if (
		isVerified(
			token.id,
			additionalVerified.map((asset) => asset.id)
		) ||
		!token.name
	) {
		return null
	}
	const claimed = token.name.trim().toLowerCase()
	for (const [id, name] of [
		...Object.entries(VERIFIED_ASSETS),
		...additionalVerified
			.filter((asset) => asset.name)
			.map((asset) => [asset.id, asset.name as string] as const)
	]) {
		if (name.toLowerCase() === claimed) {
			return id
		}
	}
	return null
}
export const DIESEL_ID = '2:0'

/// SUBFROSTAMM factory (proxy) — the only contract its pools swap through.
export const SFAMM_FACTORY_ID = '4:333052'
/// SUBFROSTAMM fee denominator (parts per million).
export const SFAMM_FEE_DENOM = 1_000_000n

/// Whether OWRouter can hop through SUBFROSTAMM (v2.5, alkane-forge
/// feat/ow-router-v2.5-sfamm). FALSE until that implementation is live on
/// proxy 2:92934: v2.3 would send a `(4:333052, …)` hop to op-3 on the
/// factory, which reverts. While false the quote engine ignores SUBFROSTAMM
/// entirely. Every swap goes through the router, so every swap pays the OW
/// fee — there is deliberately no direct-to-factory path.
export const SFAMM_VIA_ROUTER = false

/// What the quote engine may route through besides the oyl pools.
export interface RouteOptions {
	/// `/alkane/pools` `sfamm_pools`. Only used with `sfammViaRouter`.
	sfammPools?: Pool[]
	/// Router v2.5+ is live: SUBFROSTAMM hops ride OWRouter (mixed routes OK).
	sfammViaRouter?: boolean
}
const ONE_ALKANE = 100_000_000n

/// Synthetic asset id for native BTC in the swap panel. Not a pool token —
/// selecting it routes through the frBTC wrap/unwrap signer (0.1% fee).
const WRAP_FEE_PER_1000 = 1n

/// frBTC minted (or BTC returned) for a given input after the signer's 0.1%.
export function wrapOut(amountIn: bigint): bigint {
	if (amountIn <= 0n) {
		return 0n
	}
	return (amountIn * (1000n - WRAP_FEE_PER_1000)) / 1000n
}

/// Ceil inverse of `wrapOut`: smallest input that nets at least `amountOut`.
export function wrapIn(amountOut: bigint): bigint {
	if (amountOut <= 0n) {
		return 0n
	}
	const feeMul = 1000n - WRAP_FEE_PER_1000
	return (amountOut * 1000n + feeMul - 1n) / feeMul
}

/// THE one switch. While false the terminal keeps routing through the AMM
/// factory exactly as it always has: quotes carry no router fee and the swap
/// request omits `pools`, which the API reads as "use the factory path".
///
/// FLIPPED 2026-08-09: proxy 2:92934 runs v2.3 (impl 2:93495 — explicit
/// amount_in, deliver-or-revert, 0.5% fee). Cutover evidence: upgrade tx
/// b6c3357f… trace `opcode 32766 · Call successful`; prod reads op-20 fee =
/// 5; a live alkanes_simulate of op-10 (100k frBTC → 1.2196 DIESEL through
/// pool 2:77087's real reserves) delivered ~1.5% under spot — the pool's 1%
/// plus this fee, skimmed exactly as designed — with the proxy's /impl slot
/// reading 2:93495 in the same trace. Rollback is this constant back to
/// false; the API keeps the factory path whenever `pools` is absent.
export const ROUTER_ENABLED = true

/// OWRouter's take (`fee_per_1000` in contracts/ow-router), skimmed from the
/// FINAL output of a route — not per hop. Every quote the panel shows is
/// already net of it, and the contract checks `min_out` against the net amount
/// too, so the two agree by construction.
///
/// This constant is the FALLBACK, not the truth: op-21 `set_fee` can move the
/// contract's fee at any time, and a client quoting a stale one derives a
/// `min_out` the router cannot pay — it then refunds inside a successful
/// transaction, so the swap appears to do nothing at all. `/alkane/pools`
/// reports the live value read off chain; this stands in only for an API pod
/// old enough not to send it. See `routerFeeFrom` in usePools.
export const ROUTER_FEE_PER_1000 = 5n

/// The contract's own ceiling on `set_fee` (5%). A reported fee above it did
/// not come from the router.
export const ROUTER_FEE_CAP_PER_1000 = 50n

/// The router's fee on a gross output, rounded UP.
///
/// The contract FLOORS it (`mul_div(gross, fee, 1000)`), so rounding up here
/// can only ever under-promise by one base unit. That direction is the safe
/// one: the quote — and the `min_out` derived from it — can never exceed what
/// the contract actually pays, which is what would turn a good route into a
/// refund.
///
/// The fee is a PARAMETER, not a constant read from module scope, so these
/// three helpers stay pure and the live value can reach them from the server.
export function routerFee(grossOut: bigint, feePer1000 = ROUTER_FEE_PER_1000): bigint {
	if (grossOut <= 0n) {
		return 0n
	}
	return (grossOut * feePer1000 + 999n) / 1000n
}

/// Gross output minus the router's fee — what lands in the user's wallet.
export function routerNetOut(grossOut: bigint, feePer1000 = ROUTER_FEE_PER_1000): bigint {
	const net = grossOut - routerFee(grossOut, feePer1000)
	return net > 0n ? net : 0n
}

/// Smallest gross output whose NET clears `netWanted`. Exact-out quotes ask the
/// pools for a gross amount, so the fee has to be inverted before the pool math
/// runs — otherwise asking for N tokens would deliver N minus the fee.
export function routerGrossUp(netWanted: bigint, feePer1000 = ROUTER_FEE_PER_1000): bigint {
	if (netWanted <= 0n) {
		return 0n
	}
	const feeMul = 1000n - feePer1000
	let gross = (netWanted * 1000n + feeMul - 1n) / feeMul
	// Ceiling the fee can eat the single unit this estimate left; `routerNetOut`
	// is monotonic, so stepping up terminates immediately (at most a unit or two).
	while (routerNetOut(gross, feePer1000) < netWanted) {
		gross += 1n
	}
	return gross
}

export function swapOut(
	amountIn: bigint,
	reserveIn: bigint,
	reserveOut: bigint,
	feePer1000: bigint,
	feeDen = 1000n
): bigint {
	if (amountIn <= 0n || reserveIn <= 0n || reserveOut <= 0n || feePer1000 >= feeDen) {
		return 0n
	}
	const inWithFee = amountIn * (feeDen - feePer1000)
	return (inWithFee * reserveOut) / (reserveIn * feeDen + inWithFee)
}

/// Ceil inverse of `swapOut`: the smallest input that yields at least
/// `amountOut`. Null when the pool cannot produce that much (out >= reserve).
///
///   swapOut(x) >= out  ⟺  x*(1000-fee) >= ceil(out * reserveIn * 1000 / (reserveOut - out))
export function swapIn(
	amountOut: bigint,
	reserveIn: bigint,
	reserveOut: bigint,
	feePer1000: bigint,
	feeDen = 1000n
): bigint | null {
	if (amountOut <= 0n || reserveIn <= 0n || reserveOut <= 0n || amountOut >= reserveOut) {
		return null
	}
	const denom = reserveOut - amountOut
	const inWithFee = (amountOut * reserveIn * feeDen + denom - 1n) / denom
	const feeMul = feeDen - feePer1000
	if (feeMul <= 0n) {
		return null
	}
	return (inWithFee + feeMul - 1n) / feeMul
}

function hopFor(pool: Pool, tokenIn: string): Omit<Hop, 'amountIn' | 'amountOut'> | null {
	const r0 = BigInt(pool.reserve0)
	const r1 = BigInt(pool.reserve1)
	if (r0 <= 0n || r1 <= 0n) {
		return null
	}
	const venue = poolVenue(pool)
	const sfamm = venue === 'subfrostamm'
	// A SUBFROSTAMM row without a usable ppm fee cannot be quoted honestly.
	if (sfamm && !(Number.isInteger(pool.fee_ppm) && (pool.fee_ppm as number) >= 0)) {
		return null
	}
	const feeNum = sfamm ? BigInt(pool.fee_ppm as number) : BigInt(pool.fee_per_1000)
	const feeDen = sfamm ? SFAMM_FEE_DENOM : 1000n
	if (feeNum >= feeDen) {
		return null
	}
	if (pool.token0.id === tokenIn) {
		return {
			pool,
			venue,
			tokenIn,
			tokenOut: pool.token1.id,
			reserveIn: r0,
			reserveOut: r1,
			feeNum,
			feeDen
		}
	}
	if (pool.token1.id === tokenIn) {
		return {
			pool,
			venue,
			tokenIn,
			tokenOut: pool.token0.id,
			reserveIn: r1,
			reserveOut: r0,
			feeNum,
			feeDen
		}
	}
	return null
}

/// The pool each venue offers for a hop: the deepest oyl pool and the deepest
/// SUBFROSTAMM pool for the pair (one per pair there). Callers pick between
/// them on the amount actually being traded, so a deeper pool with the lower
/// fee wins on its own merits and a dust pool loses on price impact.
function venuePoolsFor(oyl: Pool[], sfamm: Pool[], a: string, b: string): Pool[] {
	const out: Pool[] = []
	const o = bestDirectPool(oyl, a, b)
	if (o) {
		out.push(o)
	}
	const sf = bestDirectPool(sfamm, a, b)
	if (sf) {
		out.push(sf)
	}
	return out
}

/// The SUBFROSTAMM pools a route may use: none until the router can reach them.
function sfammFor(opts: RouteOptions): Pool[] {
	return opts.sfammViaRouter ? opts.sfammPools ?? [] : []
}

function quoteForPath(
	pools: Pool[],
	path: string[],
	amountIn: bigint,
	routerFeePer1000: bigint,
	opts: RouteOptions = {}
): Quote | null {
	const sfamm = sfammFor(opts)
	const hops: Hop[] = []
	// Parallel to `hops`: the pools, in order, that the router is told to swap
	// through. Collected here rather than re-derived at submit time so the route
	// that was quoted is provably the route that gets signed.
	const routePools: QuotePool[] = []
	let amt = amountIn
	// 1e18-scaled depth-free spot multiplier across the path.
	const SPOT_SCALE = 10n ** 18n
	let spot = SPOT_SCALE
	for (let i = 0; i < path.length - 1; i++) {
		// Per hop, the venue that pays more for THIS amount.
		let h: Omit<Hop, 'amountIn' | 'amountOut'> | null = null
		let out = 0n
		for (const pool of venuePoolsFor(pools, sfamm, path[i], path[i + 1])) {
			const cand = hopFor(pool, path[i])
			if (!cand) {
				continue
			}
			const o = swapOut(amt, cand.reserveIn, cand.reserveOut, cand.feeNum, cand.feeDen)
			if (o > out) {
				h = cand
				out = o
			}
		}
		if (!h || out <= 0n) {
			return null
		}
		hops.push({ ...h, amountIn: amt, amountOut: out })
		// `out_slot` is about THIS pool's token ordering, not the path's: it is
		// set when the token this hop buys sits in the pool's token1 slot. A
		// SUBFROSTAMM hop names the FACTORY (its pools are router-only) and the
		// router ignores its out_slot.
		routePools.push(
			h.venue === 'subfrostamm'
				? { poolId: SFAMM_FACTORY_ID, outSlot: false }
				: { poolId: h.pool.pool_id, outSlot: h.pool.token1.id === h.tokenOut }
		)
		spot = (spot * h.reserveOut * (h.feeDen - h.feeNum)) / (h.reserveIn * h.feeDen)
		amt = out
	}
	// The router skims its fee off the final output only, so it lands here —
	// once, after the last hop — and everything downstream (rate, min received,
	// impact) reads the net figure.
	// The switch lives HERE, not inside the fee helpers: those stay pure math
	// so they remain directly testable in both states.
	const netOut = ROUTER_ENABLED ? routerNetOut(amt, routerFeePer1000) : amt
	if (netOut <= 0n) {
		return null
	}
	const spotOut = (amountIn * spot) / SPOT_SCALE
	// Impact is DEPTH cost only: spot vs the pools' GROSS fill. Measuring it
	// against the net figure folded the router's flat 0.5% into every quote —
	// double-counting it against its own summary row, and reading "0.50%
	// impact" on a dust trade that moved no price at all.
	const impact = spotOut > 0n ? Number(((spotOut - amt) * 10_000n) / spotOut) / 100 : 0
	return {
		path,
		hops,
		pools: routePools,
		amountIn,
		amountOut: netOut,
		spotOut,
		priceImpactPct: Math.max(0, impact)
	}
}

/// Deepest direct pool for a pair (several pools can exist per pair).
function bestDirectPool(pools: Pool[], a: string, b: string): Pool | null {
	let best: Pool | null = null
	let bestDepth = -1n
	for (const p of pools) {
		const pair =
			(p.token0.id === a && p.token1.id === b) || (p.token0.id === b && p.token1.id === a)
		if (!pair) {
			continue
		}
		const depth = BigInt(p.reserve0) * BigInt(p.reserve1)
		if (depth > bestDepth) {
			best = p
			bestDepth = depth
		}
	}
	return best
}

/// Best route from `tokenIn` to `tokenOut`: direct pool vs 2-hop through the
/// liquid bases (frBTC, DIESEL). Highest output wins.
///
/// `routerFeePer1000` is the LIVE fee the server read off the router; callers
/// that only want a market price (the mid-price probes below read `spotOut`,
/// which the fee never touches) can let it default.
export function bestQuote(
	pools: Pool[],
	tokenIn: string,
	tokenOut: string,
	amountIn: bigint,
	routerFeePer1000 = ROUTER_FEE_PER_1000,
	opts: RouteOptions = {}
): Quote | null {
	if (tokenIn === tokenOut || amountIn <= 0n) {
		return null
	}
	const candidates: string[][] = [[tokenIn, tokenOut]]
	for (const via of [FRBTC_ID, DIESEL_ID]) {
		if (via !== tokenIn && via !== tokenOut) {
			candidates.push([tokenIn, via, tokenOut])
		}
	}
	let best: Quote | null = null
	for (const path of candidates) {
		const q = quoteForPath(pools, path, amountIn, routerFeePer1000, opts)
		if (q && (!best || q.amountOut > best.amountOut)) {
			best = q
		}
	}
	return best
}

/// Smallest input along a path that NETS at least `amountOut`, by walking the
/// path backwards through each hop's ceil inverse.
///
/// The pools only ever see gross amounts — the router's fee comes off after the
/// last hop — so the target is grossed up before the walk begins.
function requiredInForPath(
	pools: Pool[],
	path: string[],
	amountOut: bigint,
	routerFeePer1000: bigint,
	opts: RouteOptions = {}
): bigint | null {
	const sfamm = sfammFor(opts)
	let needed = ROUTER_ENABLED ? routerGrossUp(amountOut, routerFeePer1000) : amountOut
	if (needed <= 0n) {
		return null
	}
	for (let i = path.length - 2; i >= 0; i--) {
		// Per hop, the venue that needs the LEAST input for this output.
		let best: bigint | null = null
		for (const pool of venuePoolsFor(pools, sfamm, path[i], path[i + 1])) {
			const h = hopFor(pool, path[i])
			if (!h) {
				continue
			}
			const amtIn = swapIn(needed, h.reserveIn, h.reserveOut, h.feeNum, h.feeDen)
			if (amtIn !== null && (best === null || amtIn < best)) {
				best = amtIn
			}
		}
		if (best === null) {
			return null
		}
		needed = best
	}
	return needed
}

/// Exact-out counterpart of `bestQuote`: cheapest route that delivers at least
/// `amountOut` NET of the router's fee — ask for N and you receive N, not
/// N minus the fee. The returned Quote is the exact-in forward quote of the
/// computed input, so hops/pools/impact/spot match what execution will submit.
export function bestQuoteExactOut(
	pools: Pool[],
	tokenIn: string,
	tokenOut: string,
	amountOut: bigint,
	routerFeePer1000 = ROUTER_FEE_PER_1000,
	opts: RouteOptions = {}
): Quote | null {
	if (tokenIn === tokenOut || amountOut <= 0n) {
		return null
	}
	const candidates: string[][] = [[tokenIn, tokenOut]]
	for (const via of [FRBTC_ID, DIESEL_ID]) {
		if (via !== tokenIn && via !== tokenOut) {
			candidates.push([tokenIn, via, tokenOut])
		}
	}
	let best: Quote | null = null
	for (const path of candidates) {
		const amtIn = requiredInForPath(pools, path, amountOut, routerFeePer1000, opts)
		if (amtIn === null || amtIn <= 0n) {
			continue
		}
		const q = quoteForPath(pools, path, amtIn, routerFeePer1000, opts)
		if (q && q.amountOut >= amountOut && (!best || q.amountIn < best.amountIn)) {
			best = q
		}
	}
	return best
}

/// Distinct tradable tokens across all pools with live reserves.
export function tradableTokens(pools: Pool[]): PoolToken[] {
	const seen = new Map<string, PoolToken>()
	for (const p of pools) {
		if (BigInt(p.reserve0) <= 0n || BigInt(p.reserve1) <= 0n) {
			continue
		}
		for (const t of [p.token0, p.token1]) {
			const prev = seen.get(t.id)
			if (!prev || (!prev.name && t.name)) {
				seen.set(t.id, t)
			}
		}
	}
	return Array.from(seen.values())
}

/// Mid price of `token` in frBTC base units per whole token, from the deepest
/// route to frBTC. Returns null when unpriceable.
/// Extra precision decades for the mid-price probe. Micro-priced memecoins
/// (millions of tokens per frBTC) truncate to 0 when probed with a single
/// whole token, which is why unpriceable "—" showed for real holdings; probing
/// with 1e6 tokens and scaling back keeps six extra significant digits.
const PROBE_SCALE = 1_000_000n

/// Sub-unit-precision variant: frBTC per token scaled by 1e8 (so a token
/// worth 1e-12 frBTC is still representable). Used for USD conversion.
export function frbtcMidPriceScaled(pools: Pool[], token: string): bigint | null {
	if (token === FRBTC_ID) {
		return ONE_ALKANE * ONE_ALKANE
	}
	const q = bestQuote(pools, token, FRBTC_ID, ONE_ALKANE * PROBE_SCALE)
	if (!q || q.spotOut <= 0n) {
		return null
	}
	// spotOut is frBTC base units per PROBE_SCALE tokens; scale to 1e8 units
	// per single token without losing the fraction.
	return (q.spotOut * ONE_ALKANE) / PROBE_SCALE
}

/// A pool viewed as a market: base = the asset being priced (the non-frBTC
/// side when frBTC is in the pair, else token0), quote = the pricing side.
export interface Market {
	pool: Pool
	base: PoolToken
	quote: PoolToken
}

function marketOf(pool: Pool): Market {
	if (pool.token0.id === FRBTC_ID) {
		return { pool, base: pool.token1, quote: pool.token0 }
	}
	return { pool, base: pool.token0, quote: pool.token1 }
}

/// The market that stands behind an ASSET: its deepest direct frBTC pool,
/// oriented with the asset as base. Falls back to the asset's deepest pool of
/// any kind (some tokens only trade against DIESEL or another alkane), and to
/// null for an id that trades nowhere. frBTC itself resolves to the deepest
/// frBTC pool overall — "the frBTC market" is wherever most of it lives.
export function marketForAsset(pools: Pool[], assetId: string): Market | null {
	let best: Pool | null = null
	let bestDepth = -1n
	let fallback: Pool | null = null
	let fallbackDepth = -1n
	for (const p of pools) {
		const r0 = BigInt(p.reserve0)
		const r1 = BigInt(p.reserve1)
		if (r0 <= 0n || r1 <= 0n) {
			continue
		}
		const holdsAsset = p.token0.id === assetId || p.token1.id === assetId
		if (!holdsAsset) {
			continue
		}
		if (p.token0.id === FRBTC_ID || p.token1.id === FRBTC_ID) {
			const depth = p.token0.id === FRBTC_ID ? r0 : r1
			if (depth > bestDepth) {
				best = p
				bestDepth = depth
			}
		} else if (r0 + r1 > fallbackDepth) {
			fallback = p
			fallbackDepth = r0 + r1
		}
	}
	const pool = best ?? fallback
	if (!pool) {
		return null
	}
	if (assetId === FRBTC_ID) {
		return marketOf(pool)
	}
	// Orient the ASSET as base even in a non-frBTC pool.
	if (pool.token1.id === assetId) {
		return { pool, base: pool.token1, quote: pool.token0 }
	}
	return { pool, base: pool.token0, quote: pool.token1 }
}

/// Price from a chart point's reserves, oriented for the market.
export function pointPrice(m: Market, reserve0: string, reserve1: string): number {
	const r0 = Number(reserve0)
	const r1 = Number(reserve1)
	const base = m.base.id === m.pool.token0.id ? r0 : r1
	const quote = m.quote.id === m.pool.token0.id ? r0 : r1
	if (base <= 0) {
		return 0
	}
	return quote / base
}

export function parseAmount(input: string): bigint | null {
	const trimmed = input.trim()
	if (!/^\d*\.?\d*$/.test(trimmed) || trimmed === '' || trimmed === '.') {
		return null
	}
	const [whole, frac = ''] = trimmed.split('.')
	const fracPadded = (frac + '00000000').slice(0, 8)
	try {
		return BigInt(whole || '0') * ONE_ALKANE + BigInt(fracPadded)
	} catch {
		return null
	}
}

export function formatAmount(raw: bigint, maxDp = 8): string {
	const neg = raw < 0n
	const abs = neg ? -raw : raw
	const whole = abs / ONE_ALKANE
	const frac = (abs % ONE_ALKANE).toString().padStart(8, '0').slice(0, maxDp).replace(/0+$/, '')
	const wholeStr = whole.toLocaleString('en-US')
	return `${neg ? '-' : ''}${wholeStr}${frac ? '.' + frac : ''}`
}

export function applySlippage(amountOut: bigint, slippagePct: number): bigint {
	const bps = BigInt(Math.round(slippagePct * 100))
	const floored = (amountOut * (10_000n - bps)) / 10_000n
	return floored > 0n ? floored : 1n
}
